import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Modal,
  TablePagination,
} from "@mui/material";
import { useEffect, useState } from "react";
import CustomTable from "components/custom-table/Table";
import { formatDate } from "utils/formatDate";
import { getDocumentHistory } from "../service/CompanyDocumentsService";
import DocumentActions from "./DocumentActions";
import DocumentStatus from "./DocumentStatus";
import { useCompanyDocumentsLanguage } from "../i18n/CompanyDocumentsLanguageContext";

export default function DocumentHistoryModal({
  documentType,
  latestId,
  onboardingShareholderId,
  onClose,
}) {
  const { t } = useCompanyDocumentsLanguage();
  const [data, setData] = useState();
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getDocumentHistory(documentType.id, page + 1, onboardingShareholderId)
      .then((result) => {
        if (active) setData(result);
      })
      .catch((error) => {
        if (active) setError(error.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [documentType.id, page, onboardingShareholderId]);
  return (
    <Modal open onClose={onClose} aria-labelledby="document-history-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "94%", md: 1000 },
          maxWidth: "calc(100vw - 24px)",
          maxHeight: "90vh",
          boxSizing: "border-box",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          p: 3,
        }}
      >
        <h2 id="document-history-title" className="text-xl font-bold mb-5">
          {t("client.history.title", {
            name: documentType.name,
          })}
        </h2>
        {error && <Alert severity="error">{error}</Alert>}
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          !error && (
            <>
              <CustomTable
                id="company-document-history"
                showPagination={false}
                headCells={[
                  { id: "version", label: t("client.history.version") },
                  { id: "filename", label: t("client.history.file") },
                  { id: "status", label: t("client.history.status") },
                  { id: "uploaded", label: t("client.history.uploaded") },
                  { id: "actions", label: t("client.history.actions") },
                ]}
                bodyCells={(data?.documents || []).map((doc) => ({
                  id: doc.id,
                  version: (
                    <>
                      <p>v{doc.version}</p>
                      <p className="text-xs text-[#5F737F]">
                        {doc.id === latestId
                          ? t("client.history.latest")
                          : t("client.history.previous")}
                      </p>
                    </>
                  ),
                  filename: (
                    <div className="max-w-xs break-all">
                      <p>{doc.original_filename}</p>
                      {doc.review_note && (
                        <p className="text-xs mt-1 whitespace-pre-wrap break-words">
                          {t("client.history.review")}:{" "}
                        {doc.review_note}
                        </p>
                      )}
                    </div>
                  ),
                  status: <DocumentStatus status={doc.status} />,
                  uploaded: (
                    <div>
                      <p>{formatDate.toFullDateTime(doc.uploaded_at)}</p>
                      <p className="text-xs">
                        {t("client.history.by")}:{" "}
                        {doc.uploaded_by_name ||
                          t("client.history.user", {
                            id: doc.uploaded_by,
                          })}
                      </p>
                      {doc.reviewed_at && (
                        <div className="mt-2 text-xs">
                          <p>
                            {t("client.history.reviewed")}:{" "}
                            {formatDate.toFullDateTime(doc.reviewed_at)}
                          </p>
                          <p>
                            {t("client.history.by")}:{" "}
                            {doc.reviewed_by_name ||
                              t(
                                "client.history.admin",
                                {
                                  id: doc.reviewed_by,
                                },
                              )}
                          </p>
                        </div>
                      )}
                    </div>
                  ),
                  actions: <DocumentActions document={doc} />,
                }))}
              />
              <TablePagination
                component="div"
                count={data?.total || 0}
                page={page}
                onPageChange={(_, value) => setPage(value)}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
              />
            </>
          )
        )}
        <div className="flex justify-end mt-4">
          <Button onClick={onClose}>
            {t("client.history.close")}
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
